import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ShieldAlert } from 'lucide-react'
import { riskApi } from '../api/client'
import type { DrawdownFloorEvent, DrawdownFloorExposure, DrawdownFloorStatus } from '../types'
import './RiskBackstopCard.css'

interface RiskBackstopCardProps {
  accountId: number
}

// Drawdown, floor, headroom and exposure target all arrive as FRACTIONS (0.14 = 14%).
const pct = (f: number) => `${(f * 100).toFixed(1)}%`
const pctWhole = (f: number) => `${Math.round(f * 100)}%`
const localDT = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

// request() throws Error(<raw response text>); FastAPI error bodies are {"detail": "..."}.
// Show the server's detail string verbatim, falling back to the raw text.
function errorDetail(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err)
  try {
    const body = JSON.parse(message)
    if (body && typeof body.detail === 'string') return body.detail
  } catch {
    // not JSON — fall through to the raw text
  }
  return message
}

function drawdownLine(data: DrawdownFloorStatus): string {
  if (data.drawdown != null) {
    const head = data.headroom != null ? data.headroom : data.floor - data.drawdown
    const headText = head >= 0
      ? `${(head * 100).toFixed(1)} pts headroom`
      : `${(-head * 100).toFixed(1)} pts beyond the floor`
    return `${pct(data.drawdown)} of ${pct(data.floor)} · ${headText}`
  }
  if (data.last_known_drawdown != null) {
    const at = data.last_known_at ? ` (at ${localDT(data.last_known_at)})` : ''
    return `market closed — last known ${pct(data.last_known_drawdown)}${at}`
  }
  return 'no reading yet'
}

function eventLine(ev: DrawdownFloorEvent): string {
  const note = ev.note ? ` — ${ev.note}` : ''
  return `Last event: ${ev.event} ${localDT(ev.created_at)} by ${ev.actor} ` +
    `(drawdown ${pct(ev.drawdown)}, floor ${pct(ev.floor)})${note}`
}

function exposureLine(exposure: DrawdownFloorExposure | null): string {
  const target = exposure?.target ?? null
  if (exposure?.enabled) {
    return target != null
      ? `Exposure scaling: On — target ${pctWhole(target)} invested (shadow: ${pctWhole(target)})`
      : 'Exposure scaling: On'
  }
  return target != null
    ? `Exposure scaling: Off (shadow target ${pctWhole(target)})`
    : 'Exposure scaling: Off'
}

function ClearControl({ accountId }: { accountId: number }) {
  const qc = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const [note, setNote] = useState('')
  const clear = useMutation({
    mutationFn: () => riskApi.clearDrawdownFloor(accountId, note.trim()),
    onSuccess: () => {
      setConfirming(false)
      setNote('')
      qc.invalidateQueries({ queryKey: ['drawdown-floor', accountId] })
    },
  })

  if (!confirming) {
    return (
      <div className="risk-backstop-actions">
        {clear.error != null && (
          <div className="risk-backstop-error" role="alert">{errorDetail(clear.error)}</div>
        )}
        <button
          type="button"
          className="btn btn-sm btn-danger"
          onClick={() => { clear.reset(); setConfirming(true) }}
        >
          Clear floor
        </button>
      </div>
    )
  }

  return (
    <div className="risk-backstop-actions">
      <p className="caption">
        Clearing re-opens new entries and top-ups on this book. Make sure drawdown is back
        under the floor, or raise the per-book floor first.
      </p>
      <label className="risk-backstop-note">
        <span className="caption">Note (optional)</span>
        <input
          type="text"
          className="input"
          maxLength={255}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      {clear.error != null && (
        <div className="risk-backstop-error" role="alert">{errorDetail(clear.error)}</div>
      )}
      <div className="risk-backstop-confirm-row">
        <button
          type="button"
          className="btn btn-sm btn-danger"
          disabled={clear.isPending}
          onClick={() => clear.mutate()}
        >
          Confirm clear
        </button>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          disabled={clear.isPending}
          onClick={() => { clear.reset(); setConfirming(false) }}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

/**
 * Per-book drawdown-floor status (wargame 22): armed/tripped, drawdown vs floor with
 * headroom, the last floor event, the admin-only clear control while tripped, and the
 * exposure-scaling (shadow) line. A failed query shows a caption only — it must never
 * take the rest of the book page down.
 */
export function RiskBackstopCard({ accountId }: RiskBackstopCardProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['drawdown-floor', accountId],
    queryFn: () => riskApi.drawdownFloor(accountId),
    retry: 1,
    refetchInterval: 60000,
  })

  const tripped = data?.tripped === true
  const off = data != null && !data.enabled && !tripped
  const pillCls = tripped ? 'tripped' : off ? 'off' : 'armed'
  const pillText = tripped ? 'Tripped' : off ? 'Off' : 'Armed'

  return (
    <div className="card risk-backstop-card">
      <div className="card-header">
        <h3 className="card-title">
          <ShieldAlert size={18} className="icon" aria-hidden="true" />
          Risk backstop
        </h3>
        {data && (
          <span className={`risk-backstop-pill risk-backstop-pill--${pillCls}`}>
            <span className="risk-backstop-pill-dot" aria-hidden="true" />
            {pillText}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : !data ? (
        <div className="empty-state">
          <div className="empty-icon"><ShieldAlert size={28} aria-hidden="true" /></div>
          <div className="empty-text">risk backstop unavailable</div>
        </div>
      ) : (
        <>
          {isError && <p className="caption risk-backstop-stale">stale — refresh failed</p>}
          <p className="risk-backstop-drawdown num">{drawdownLine(data)}</p>
          <p className="caption">
            Floor source: {data.floor_source} · blocks new entries and top-ups; exits always run
          </p>
          <p className="caption">
            {data.last_event ? eventLine(data.last_event) : 'No floor events yet'}
          </p>
          {tripped && <ClearControl accountId={accountId} />}
          <p className="caption risk-backstop-exposure">{exposureLine(data.exposure)}</p>
        </>
      )}
    </div>
  )
}
